import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { Reveal, SectionLabel } from "./Motion";
import { ScanLine, Thermometer, Droplets, CalendarCheck, Check, Play, Signal, Wifi, BatteryFull } from "lucide-react";

const PhoneDemo = () => {
  const { t } = useLang();
  const d = t.demo;
  const [step, setStep] = useState(0);
  const [temp, setTemp] = useState(4.2);
  const [hum, setHum] = useState(58);

  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), 2600);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setTemp(+(3.8 + Math.random() * 1.2).toFixed(1));
      setHum(55 + Math.floor(Math.random() * 8));
    }, 1400);
    return () => clearInterval(id);
  }, []);

  const s = d.steps[step];

  return (
    <div className="relative mx-auto w-[300px] md:w-[330px]">
      {/* phone frame */}
      <div className="relative rounded-[2.6rem] border-[10px] border-[#12241b] bg-[#12241b] shadow-[0_40px_90px_-30px_rgba(30,43,35,0.5)]">
        <div className="relative rounded-[2rem] overflow-hidden bg-white h-[620px]">
          {/* notch */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#12241b] rounded-b-2xl z-20" />
          {/* status bar */}
          <div className="flex items-center justify-between px-6 pt-3 text-[10px] text-[var(--text-primary)] font-jetbrains">
            <span>09:41</span>
            <div className="flex items-center gap-1">
              <Signal className="w-3 h-3" /><Wifi className="w-3 h-3" /><BatteryFull className="w-3.5 h-3.5" />
            </div>
          </div>
          {/* app header */}
          <div className="px-5 pt-4">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-[var(--gold)]/15 flex items-center justify-center">
                <ScanLine className="w-4 h-4 text-[var(--gold)]" />
              </span>
              <div>
                <div className="font-outfit font-semibold text-sm text-[var(--text-primary)] leading-none">Amazonico3000</div>
                <div className="overline text-[8px] text-[var(--text-secondary)] mt-1">DIGITAL TWIN</div>
              </div>
            </div>
            <div className="mt-4 rounded-2xl bg-[var(--bg-secondary)] p-4 border border-[var(--border)]">
              <div className="overline text-[8px] text-[var(--gold)]">{d.product}</div>
              <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--green)] animate-pulse" />
                <span className="font-jetbrains text-[10px] text-[var(--text-secondary)]">ID: A1B2C3D4E5F6</span>
              </div>
            </div>
          </div>

          {/* animated step */}
          <div className="px-5 mt-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.45 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="overline text-[8px] text-[var(--gold)]">{s.tag}</span>
                  <span className="h-px flex-1 bg-[var(--border)]" />
                </div>

                {step === 0 && (
                  <div className="rounded-2xl border border-[var(--border)] p-4">
                    <div className="font-outfit text-sm text-[var(--text-primary)]">{s.title}</div>
                    <div className="overline text-[8px] text-[var(--text-secondary)] mt-1">{s.sub}</div>
                    <div className="mt-4 h-1.5 rounded-full bg-[var(--bg-secondary)] overflow-hidden">
                      <motion.div className="h-full bg-[var(--green)]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 2.2 }} />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-[var(--border)] p-4">
                      <Thermometer className="w-4 h-4 text-[var(--gold)]" />
                      <div className="font-cormorant text-3xl text-[var(--text-primary)] mt-2">{temp}°</div>
                      <div className="overline text-[8px] text-[var(--text-secondary)]">Temp</div>
                    </div>
                    <div className="rounded-2xl border border-[var(--border)] p-4">
                      <Droplets className="w-4 h-4 text-[var(--gold)]" />
                      <div className="font-cormorant text-3xl text-[var(--text-primary)] mt-2">{hum}%</div>
                      <div className="overline text-[8px] text-[var(--text-secondary)]">Umid.</div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="rounded-2xl border border-[var(--green)]/40 bg-[var(--green)]/5 p-4">
                    <div className="flex items-center gap-2">
                      <CalendarCheck className="w-4 h-4 text-[var(--green)]" />
                      <span className="overline text-[8px] text-[var(--text-secondary)]">{d.expLabel}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="font-jetbrains text-sm text-[var(--text-secondary)] line-through">{d.expOld}</span>
                      <motion.span
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="font-cormorant text-2xl text-[var(--text-primary)]"
                      >
                        → {d.expNew}
                      </motion.span>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--green)] text-white px-3 py-1.5">
                      <Check className="w-3.5 h-3.5" />
                      <span className="font-outfit text-[11px] font-medium">{d.fresh}</span>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* step dots */}
          <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-[var(--gold)]" : "w-2 bg-[var(--border)]"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const VideoCard = ({ item, delay }) => (
  <Reveal delay={delay}>
    <div className="group relative overflow-hidden rounded-[1.6rem] border border-[var(--border)] h-[300px]">
      <img src={IMAGES[item.img]} alt={item.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(20,36,27,0.85)] via-transparent to-transparent" />
      {/* scanning line = video feel */}
      <motion.div
        animate={{ top: ["10%", "86%", "10%"] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-4 right-4 h-px bg-[var(--green)]/60 shadow-[0_0_16px_2px_rgba(60,122,78,0.5)]"
      />
      <div className="absolute top-4 left-4 flex items-center gap-2 glass rounded-full px-2.5 py-1">
        <Play className="w-3 h-3 text-[var(--gold)]" fill="currentColor" />
        <span className="font-jetbrains text-[9px] tracking-widest text-[var(--text-primary)]">AI · LIVE</span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h4 className="font-cormorant text-2xl text-white">{item.t}</h4>
        <p className="font-outfit font-light text-xs text-white/80 mt-1">{item.d}</p>
      </div>
    </div>
  </Reveal>
);

export const AppDemo = () => {
  const { t } = useLang();
  const d = t.demo;
  return (
    <section id="demo" data-testid="demo-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-1/4 right-0 w-[480px] h-[480px] rounded-full bg-[var(--green)]/8 blur-[150px] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionLabel>{d.label}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
              {d.title}
            </h2>
            <p className="font-outfit font-light text-base md:text-lg text-[var(--text-secondary)] max-w-lg mt-6 leading-relaxed">
              {d.intro}
            </p>
            <div className="mt-8 space-y-3">
              {d.steps.map((st, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg-secondary)]/50 px-5 py-4">
                    <span className="font-jetbrains text-xs text-[var(--gold)]">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="font-outfit text-sm text-[var(--text-primary)]">{st.title}</div>
                      <div className="overline text-[9px] text-[var(--text-secondary)] mt-0.5">{st.sub}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 flex justify-center">
            <Reveal>
              <PhoneDemo />
            </Reveal>
          </div>
        </div>

        {/* più video */}
        <div className="grid md:grid-cols-3 gap-6 mt-20">
          {d.videos.map((v, i) => (
            <VideoCard key={i} item={v} delay={i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
};
